import React, { useEffect } from "react";
import { Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLShipmentListForSendOutQuery } from "store/services/sendOutApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";

import { useAuth } from "hooks";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AppSwitch from "components/Shared/AppSwitch";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: [],
  assignmentType: "",
  driverId: "",
  vehicle_id: "",
  responsible: "",
  description: "",
  isActive: true,
};

const SendOutForm = ({
  isBusy = false,
  sendOutDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchDriver = useWatch({ control, name: "driverId" });
  const _watchAssignmentType = useWatch({ control, name: "assignmentType" });
  const driverVehicles = _watchDriver?.assigned_vehicle_ids || [];

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentListForSendOutQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();
  const [fetchUsers, { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers }] =
    useLazyGetDDLSystemUserListQuery();

  const availableShipments = shipments
    ?.filter(
      (ship) =>
        (ship.status === "Deconsolidation" ||
          ship.status === "DeliveryFailed" ||
          ship.status === "Unloaded" ||
          ship.status === "Warehousing" ||
          ship.status === "Return") &&
        ship.isOnHold !== true
    )
    ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const _mappedUsers = React.useMemo(
    () =>
      userList?.map((el) => ({
        label: el?.name,
        id: el?._id,
      })) || [],
    [userList]
  );

  const handleOnSubmit = (data) => {
    const payload = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      shipmentId: data?.shipmentId?.map((item) => item?._id),
      driverId: data?.assignmentType === "DriverType" ? data?.driverId?._id : undefined,
      vehicle_id: data?.assignmentType === "DriverType" ? data?.vehicle_id?._id : undefined,
      responsibleUser: data?.assignmentType === "UserType" ? data?.responsible?.id : undefined,
    };
    onSubmit(payload);
  };

  useEffect(() => {
    if (sendOutDetail) {
      reset({
        ...sendOutDetail,
        account: sendOutDetail.account,
        entity: sendOutDetail.entity,
        shipmentId: sendOutDetail.shipmentId || [],
        assignmentType: sendOutDetail.driverId ? "DriverType" : sendOutDetail.responsibleUser ? "UserType" : "",
        driverId: sendOutDetail.driverId ? {
          _id: sendOutDetail.driverId._id,
          driverId: sendOutDetail.driverId.driverId,
          name: sendOutDetail.driverId.name,
          assigned_vehicle_ids: sendOutDetail.driverId.assigned_vehicle_ids,
        } : "",
        vehicle_id: sendOutDetail.vehicle_id ? {
          _id: sendOutDetail.vehicle_id._id,
          vehicle_id: sendOutDetail.vehicle_id.vehicle_id,
          registration_number: sendOutDetail.vehicle_id.registration_number,
        } : "",
        responsible: sendOutDetail.responsibleUser ? {
          label: sendOutDetail.responsibleUser.name,
          id: sendOutDetail.responsibleUser._id,
        } : "",
      });
    }
  }, [sendOutDetail, reset]);

  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount, fetchEntities, isAccountAdmin, isSystemAdmin]);

  useEffect(() => {
    if (_watchEntity?._id) {
      fetchShipments({ account: _watchAccount?._id, entity: _watchEntity._id });
      fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity._id });
      fetchUsers({ entity: _watchEntity._id });
    }
  }, [_watchEntity, _watchAccount, fetchShipments, fetchDrivers, fetchUsers]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Account is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, value) => {
                      field.onChange(value);
                      setValue("entity", "");
                      setValue("shipmentId", []);
                      setValue("assignmentType", "");
                      setValue("driverId", "");
                      setValue("vehicle_id", "");
                      setValue("responsible", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    disabled={isAccountAdmin}
                    loadingText="Loading Accounts..."
                    error={!!errors?.account}
                    helperText={errors?.account?.message}
                  />
                )}
              />
            </Grid>
          )}

          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item xs={12} md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Entity is required" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Entity"
                    required
                    value={field.value ?? ""}
                    onChange={(_, value) => {
                      field.onChange(value);
                      setValue("shipmentId", []);
                      setValue("assignmentType", "");
                      setValue("driverId", "");
                      setValue("vehicle_id", "");
                      setValue("responsible", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    error={!!errors?.entity}
                    helperText={errors?.entity?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Controller
              name="assignmentType"
              control={control}
              rules={{ required: "Assignment Type is required" }}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors?.assignmentType}>
                  <InputLabel id="assignment-type-label">Assignment Type</InputLabel>
                  <Select
                    labelId="assignment-type-label"
                    label="Assignment Type"
                    value={field.value || ""}
                    onChange={(e) => {
                      field.onChange(e);
                      setValue("driverId", "");
                      setValue("vehicle_id", "");
                      setValue("responsible", "");
                    }}
                    required
                  >
                    <MenuItem value="DriverType">Select Driver</MenuItem>
                    <MenuItem value="UserType">Select User</MenuItem>
                  </Select>
                  {errors.assignmentType && (
                    <FormHelperText error>{errors.assignmentType.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {_watchAssignmentType === "DriverType" && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name="driverId"
                  control={control}
                  rules={{ required: _watchAssignmentType === "DriverType" ? "Please select Driver ID." : false }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Driver"
                      required={_watchAssignmentType === "DriverType"}
                      value={
                        drivers?.find((driver) => driver?._id === value?._id) ||
                        null
                      }
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("vehicle_id", null);
                      }}
                      getOptionLabel={(option) =>
                        option && `${option?.driverId} - ${option?.name}`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={drivers || []}
                      loading={loadingDrivers}
                      loadingText="Loading Drivers..."
                      helperText={errors?.driverId?.message}
                      error={!!errors?.driverId}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="vehicle_id"
                  control={control}
                  rules={{ required: _watchAssignmentType === "DriverType" ? "Please select Vehicle ID." : false }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Vehicle"
                      required={_watchAssignmentType === "DriverType"}
                      value={value ?? ""}
                      onChange={(_, data) => onChange(data)}
                      getOptionLabel={(option) =>
                        option
                          ? `${option?.vehicle_id} - ${option?.registration_number}`
                          : ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={driverVehicles || []}
                      disabled={!_watchDriver}
                      loadingText="Loading Vehicles..."
                      helperText={errors?.vehicle_id?.message}
                      error={!!errors?.vehicle_id}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {_watchAssignmentType === "UserType" && (
            <Grid item xs={12} md={6}>
              <Controller
                name="responsible"
                control={control}
                rules={{ required: _watchAssignmentType === "UserType" ? "Please select a Responsible User." : false }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required={_watchAssignmentType === "UserType"}
                    label="Responsible"
                    loading={loadingUsers || fetchingUsers}
                    loadingText="Loading Responsible Users..."
                    options={_mappedUsers}
                    value={field.value || null}
                    onChange={(_, data) => {
                      field.onChange(data);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id
                    }
                    error={!!errors?.responsible}
                    helperText={errors?.responsible?.message}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "At least one Shipment ID is required" }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple
                  label="Shipment IDs"
                  required
                  value={field.value ?? []}
                  onChange={(_, value) => field.onChange(value)}
                  getOptionLabel={(option) =>
                    option?.shipmentId
                      ? option.IsReturning
                        ? `${option.shipmentId} (R)`
                        : option.shipmentId
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={availableShipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipments..."
                  error={!!errors?.shipmentId}
                  helperText={errors?.shipmentId?.message}
                  disabled={!!sendOutDetail}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                />
              )}
            />
          </Grid>

          {sendOutDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          {sendOutDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default SendOutForm;